/* NZS Drag Scroll — the "Frequently paired with" carousel affordance, made shareable.

   Donor: sections/nzs-paired-with.liquid (.nzs-bs__track + NZS-PAIRED-SCROLLBAR rail).
   That donor gives touch swipe + scroll-snap + a progress rail, but its rail JS is bound
   per-section and it has NO mouse click-drag. This module is the same behaviour, plus the
   grab-to-drag, delivered by DOCUMENT-LEVEL DELEGATION so it needs zero per-element init.

   Why delegation and not per-element binding: three of the four target surfaces are re-rendered
   after load and would silently lose any bound handler --
     - recently-viewed.js does `el.innerHTML = resultsMarkup` on the whole section,
     - the cart drawer upsell row is re-rendered by Alpine / section rendering,
     - the cart-page upsell teleports recommendation cards in with x-teleport.
   Everything below reads state off the DOM at event time, so re-rendered markup just works.

   OPT-IN MARKUP
     <div data-nzs-grab> ... </div>                      <- the horizontal scroller
     <div data-nzs-rail hidden><span data-nzs-thumb></span></div>   <- optional progress rail,
                                                            a SIBLING of the scroller

   TOUCH IS UNTOUCHED. Only pointerType === 'mouse' is handled, so native touch scrolling (and
   therefore vertical page scroll) keeps its default behaviour -- there is no touchmove listener
   here at all, let alone a preventDefault on one.

   SMOOTHNESS (2026-08-12): grab-drag is 1:1 and momentum-flicked.
     - During a drag the scroller's `scroll-snap-type` AND `scroll-behavior` are both parked to
       `none`/`auto`. scroll-behavior:smooth (set on .nzs-bs__track) makes every manual scrollLeft
       write ANIMATE toward its target, so a fast drag rubber-bands behind the cursor -- parking it
       makes the content track the pointer exactly.
     - Each pointermove records the target and defers the scrollLeft write + rail redraw to one
       rAF, so a 1000Hz mouse can't force several reflow-inducing writes per frame.
     - On release the pointer velocity carries the scroller with friction decay, then snap +
       smooth are restored so the browser settles onto the nearest card. Honours reduced-motion.
*/
(function () {
  'use strict';
  if (window.__nzsDragScroll) return;   // second include must not double-bind
  window.__nzsDragScroll = true;

  var GRAB = '[data-nzs-grab]';
  var RAIL = '[data-nzs-rail]';
  var DRAG_THRESHOLD = 4;    // px before a press becomes a drag
  var MIN_THUMB = 40;        // px, matches the donor rail
  var FRICTION = 0.94;       // per-frame velocity decay for the release glide
  var MAX_FLICK = 42;        // px/frame cap so a hard flick can't fling the whole rail
  var STOP_V = 0.35;         // px/frame below which the glide ends and snap re-engages

  function nowMs() {
    return (window.performance && performance.now) ? performance.now() : Date.now();
  }
  function prefersReduce() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* ---------- rail geometry (stateless: everything is read off the DOM) ---------- */

  function railFor(scroller) {
    if (!scroller || !scroller.parentNode) return null;
    return scroller.parentNode.querySelector(RAIL);
  }

  function scrollerFor(rail) {
    if (!rail || !rail.parentNode) return null;
    return rail.parentNode.querySelector(GRAB);
  }

  function renderRail(scroller) {
    var maxScroll = scroller.scrollWidth - scroller.clientWidth;
    /* Drives the grab cursor. Some targets are only horizontal scrollers at some breakpoints -- the
       cart-drawer upsell row is `overflow-x-auto` on mobile but `lg:grid lg:overflow-x-hidden` on
       desktop -- so the cursor must follow measured scrollability, not the attribute. */
    scroller.classList.toggle('nzs-grab-on', maxScroll > 1);

    var rail = railFor(scroller);
    if (!rail) return;
    var thumb = rail.querySelector('[data-nzs-thumb]');
    if (!thumb) return;

    if (maxScroll <= 1) { rail.setAttribute('hidden', ''); return; }
    rail.removeAttribute('hidden');

    var barW = rail.clientWidth;
    var thumbW = Math.max(MIN_THUMB, Math.round((scroller.clientWidth / scroller.scrollWidth) * barW));
    thumb.style.width = thumbW + 'px';
    thumb.style.transform = 'translateX(' + ((scroller.scrollLeft / maxScroll) * (barW - thumbW)) + 'px)';
  }

  function renderAll() {
    var list = document.querySelectorAll(GRAB);
    for (var i = 0; i < list.length; i++) renderRail(list[i]);
  }

  /* ---------- park / restore the scroller's snap + smooth for manual control ---------- */

  function parkFor(el) {
    return { snap: el.style.scrollSnapType, beh: el.style.scrollBehavior };
  }
  function applyPark(el) {
    /* scroll-snap fights a manual scrollLeft write; scroll-behavior:smooth makes each write animate.
       Both must be off for the drag to track the pointer 1:1. */
    el.style.scrollSnapType = 'none';
    el.style.scrollBehavior = 'auto';
  }
  function restorePark(el, saved) {
    /* restore behaviour BEFORE snap so the re-snap (triggered by re-enabling mandatory snap) can
       animate smoothly to the nearest card. */
    if (saved.beh) { el.style.scrollBehavior = saved.beh; } else { el.style.removeProperty('scroll-behavior'); }
    if (saved.snap) { el.style.scrollSnapType = saved.snap; } else { el.style.removeProperty('scroll-snap-type'); }
  }

  /* ---------- grab-to-drag on the scroller (mouse only) ---------- */

  var drag = null;    // { el, startX, startLeft, moved, saved, lastX, lastT, vx, target, raf }
  var glide = null;   // { el, raf, saved }  -- an in-flight release momentum animation

  function stopGlide() {
    if (!glide) return;
    if (glide.raf) cancelAnimationFrame(glide.raf);
    restorePark(glide.el, glide.saved);
    renderRail(glide.el);
    glide = null;
  }

  document.addEventListener('pointerdown', function (e) {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    var el = e.target.closest && e.target.closest(GRAB);
    if (!el) return;
    if (e.target.closest(RAIL)) return;            // the rail has its own handler below
    if (el.scrollWidth - el.clientWidth <= 1) return;
    /* Grabbing mid-glide cancels the momentum (and restores snap/smooth); the drag re-parks them on
       its first real move. */
    stopGlide();
    /* No preventDefault here: a plain click on a card/link/Quick-add must still work. The drag
       only takes over once the pointer has actually travelled DRAG_THRESHOLD px. */
    drag = { el: el, startX: e.clientX, startLeft: el.scrollLeft, moved: false, saved: null,
             lastX: e.clientX, lastT: nowMs(), vx: 0, target: el.scrollLeft, raf: 0 };
  });

  function applyDrag() {
    if (!drag) return;
    drag.raf = 0;
    drag.el.scrollLeft = drag.target;
    renderRail(drag.el);
  }

  document.addEventListener('pointermove', function (e) {
    if (!drag) return;
    /* button released outside the viewport: no pointerup ever arrives, so the first
       buttons-free move is the release signal */
    if (e.buttons === 0) return endDrag();
    var dx = e.clientX - drag.startX;
    if (!drag.moved) {
      if (Math.abs(dx) <= DRAG_THRESHOLD) return;
      drag.moved = true;
      drag.el.classList.add('nzs-grabbing');
      drag.saved = parkFor(drag.el);
      applyPark(drag.el);
    }
    /* pointer velocity, sampled per event in px/ms -- feeds the release glide */
    var t = nowMs();
    var dt = t - drag.lastT;
    if (dt > 0) {
      drag.vx = (e.clientX - drag.lastX) / dt;
      drag.lastX = e.clientX;
      drag.lastT = t;
    }
    /* defer the actual scroll write + rail redraw to one frame, coalescing bursts of high-frequency
       pointermove events into a single reflow per frame */
    drag.target = drag.startLeft - dx;
    if (!drag.raf) drag.raf = requestAnimationFrame(applyDrag);
    e.preventDefault();   // mouse only -- never reached for touch/pen
  });

  function endDrag() {
    if (!drag) return;
    var d = drag;
    drag = null;
    if (d.raf) { cancelAnimationFrame(d.raf); d.el.scrollLeft = d.target; }
    /* cleanup runs unconditionally: a stale nzs-grabbing (e.g. release outside the window)
       would leave the row's images pointer-events:none until the next full drag */
    d.el.classList.remove('nzs-grabbing');
    if (!d.moved) return;
    /* Swallow the click the browser fires at the end of the drag gesture -- otherwise releasing
       on a card would follow its link. Flag + permanent capture listener (not a self-removing
       one): the flag is set only when a real drag just ended, cleared on the next pointerdown,
       so it can never eat an unrelated later click, and it wins regardless of whether the
       browser dispatches the click before or after a timeout task. */
    justDragged = true;
    setTimeout(function () { justDragged = false; }, 120);

    /* scroll velocity is OPPOSITE the pointer's clientX velocity: dragging left lowers clientX
       (vx < 0) but raises scrollLeft, so the momentum that continues that scroll is -vx. */
    var v = -d.vx * 16.67;   // px/ms -> px/frame
    if (v > MAX_FLICK) v = MAX_FLICK; else if (v < -MAX_FLICK) v = -MAX_FLICK;

    /* Weak flick or reduced-motion: no glide, just restore snap+smooth and let the browser snap. */
    if (prefersReduce() || Math.abs(v) < 1.2) {
      restorePark(d.el, d.saved);
      setTimeout(function () { renderRail(d.el); }, 0);   // let the snap settle, then redraw
      return;
    }

    /* Momentum: keep snap+smooth parked, glide with friction, restore (=> re-snap) at rest. */
    var el = d.el;
    glide = { el: el, raf: 0, saved: d.saved };
    var maxScroll = el.scrollWidth - el.clientWidth;
    function step() {
      if (!glide) return;
      v *= FRICTION;
      var next = el.scrollLeft + v;
      if (next <= 0) { el.scrollLeft = 0; return stopGlide(); }
      if (next >= maxScroll) { el.scrollLeft = maxScroll; return stopGlide(); }
      el.scrollLeft = next;
      renderRail(el);
      if (Math.abs(v) < STOP_V) return stopGlide();
      glide.raf = requestAnimationFrame(step);
    }
    glide.raf = requestAnimationFrame(step);
  }

  var justDragged = false;
  document.addEventListener('click', function (ev) {
    if (!justDragged) return;
    justDragged = false;
    ev.stopPropagation();
    ev.preventDefault();
  }, true);

  document.addEventListener('pointerup', endDrag);
  document.addEventListener('pointercancel', endDrag);
  window.addEventListener('blur', endDrag);

  /* NZS-GRAB-IMG-0901 (Blake): let the mouse grab-drag begin on the card IMAGE too.
     Native <img>/<a> dragging fires on pointerdown-over-the-photo and pre-empts the
     pointermove stream, so a press starting on the image never became a swipe. Cancel
     the browser's native drag anywhere inside a grabber. */
  document.addEventListener('dragstart', function (e) {
    if (e.target && e.target.closest && e.target.closest(GRAB)) e.preventDefault();
  });

  /* ---------- rail: drag the thumb, click the track to jump ---------- */

  var railDrag = null;   // { rail, scroller, startX, startLeft, barW, thumbW, maxScroll }

  function railMetrics(rail, scroller) {
    var thumb = rail.querySelector('[data-nzs-thumb]');
    var barW = rail.clientWidth;
    return {
      barW: barW,
      thumbW: thumb ? thumb.getBoundingClientRect().width : MIN_THUMB,
      maxScroll: scroller.scrollWidth - scroller.clientWidth
    };
  }

  document.addEventListener('pointerdown', function (e) {
    var rail = e.target.closest && e.target.closest(RAIL);
    if (!rail) return;
    var scroller = scrollerFor(rail);
    if (!scroller) return;
    stopGlide();   // grabbing the rail also cancels any momentum on its scroller
    var m = railMetrics(rail, scroller);
    if (m.maxScroll <= 1 || m.barW - m.thumbW <= 0) return;

    if (e.target.closest('[data-nzs-thumb]')) {
      railDrag = { rail: rail, scroller: scroller, startX: e.clientX, startLeft: scroller.scrollLeft,
                   barW: m.barW, thumbW: m.thumbW, maxScroll: m.maxScroll };
      try { rail.setPointerCapture(e.pointerId); } catch (_) { }
      e.preventDefault();
    } else {
      var p = (e.clientX - rail.getBoundingClientRect().left - m.thumbW / 2) / (m.barW - m.thumbW);
      /* scrollTo options override the CSS scroll-behavior property, so reduced-motion
         must be honoured here, not in the stylesheet */
      var reduce = prefersReduce();
      scroller.scrollTo({ left: Math.min(1, Math.max(0, p)) * m.maxScroll, behavior: reduce ? 'auto' : 'smooth' });
    }
  });

  document.addEventListener('pointermove', function (e) {
    if (!railDrag) return;
    var r = railDrag;
    r.scroller.scrollLeft = r.startLeft + (e.clientX - r.startX) * (r.maxScroll / (r.barW - r.thumbW));
    renderRail(r.scroller);
  });

  function endRailDrag(e) {
    if (!railDrag) return;
    try { railDrag.rail.releasePointerCapture(e.pointerId); } catch (_) { }
    railDrag = null;
  }
  document.addEventListener('pointerup', endRailDrag);
  document.addEventListener('pointercancel', endRailDrag);

  /* ---------- keep the rail in sync ---------- */

  /* scroll does not bubble, but it does capture -- so one capture-phase listener covers every
     scroller on the page, including ones injected later. */
  document.addEventListener('scroll', function (e) {
    var el = e.target;
    if (el && el.nodeType === 1 && el.matches && el.matches(GRAB)) renderRail(el);
  }, true);

  /* Trailing debounce, NOT rAF: renderAll() reads scrollWidth/clientWidth, so running it once per
     animation frame off a whole-document MutationObserver would force a reflow every frame on an
     Alpine-heavy page. Live scrolling is already covered by the capture listener above; this path
     only needs to catch "the DOM changed, re-measure eventually". */
  var timer = null;
  function schedule() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(function () { timer = null; renderAll(); }, 150);
  }

  window.addEventListener('resize', schedule);
  window.addEventListener('load', schedule);
  document.addEventListener('shopify:section:load', schedule);

  /* ponytail: the whole-document childList+subtree observer is deleted — it ran on EVERY DOM
     mutation of every page (an Alpine-heavy storefront mutates constantly) just to keep rail
     geometry "eventually" fresh. The grab cursor and rail only matter while the pointer is over
     the scroller, so re-measure exactly then: one delegated enter-style pointerover. Scrolling
     already redraws via the capture scroll listener; sizing via resize/load/section:load/DCL. */
  document.addEventListener('pointerover', function (e) {
    var el = e.target.closest && e.target.closest(GRAB);
    if (el && (!e.relatedTarget || !el.contains(e.relatedTarget))) renderRail(el);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
})();
